const BASE = "/api/dashboard";
import { removeCurrentWorkspaceToken, tokenForCurrentRoute } from "./workspaces";

function getToken(): string | null {
  return tokenForCurrentRoute();
}

function handleUnauthorized() {
  removeCurrentWorkspaceToken();
  window.location.href = "/workspaces";
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function api<T = unknown>(
  path: string,
  opts: RequestInit = {},
): Promise<{ success: boolean; data?: T; error?: { key?: string; message: string }; pagination?: any }> {
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...opts.headers,
    },
  });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error("Unauthorized");
  }
  if (res.headers.get("content-type")?.includes("text/csv")) {
    const text = await res.text();
    return { success: true, data: text as unknown as T };
  }
  return res.json();
}

export function apiGet<T = unknown>(path: string) {
  return api<T>(path);
}

export function apiPost<T = unknown>(path: string, body: unknown) {
  return api<T>(path, { method: "POST", body: JSON.stringify(body) });
}

export function apiPatch<T = unknown>(path: string, body: unknown) {
  return api<T>(path, { method: "PATCH", body: JSON.stringify(body) });
}

export function apiDelete(path: string) {
  return api(path, { method: "DELETE" });
}

/**
 * Every binary/download route is fetched with the Authorization header — a
 * dashboard JWT covers the tenant's whole account, so it must never travel in a
 * URL where the origin's access log, browser history and Referer would keep it.
 */
async function fetchAuthed(path: string, opts: RequestInit = {}): Promise<Response> {
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: { ...authHeaders(), ...opts.headers },
  });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error("Unauthorized");
  }
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return res;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  // Revoking in the same tick can abort the download the click just started.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function fetchQrObjectUrl(locationId: string, format: "svg" | "png" = "svg"): Promise<string> {
  const res = await fetchAuthed(`/locations/${locationId}/qr?format=${format}`);
  return URL.createObjectURL(await res.blob());
}

export async function downloadQr(locationId: string, format: "svg" | "png"): Promise<void> {
  const res = await fetchAuthed(`/locations/${locationId}/qr?format=${format}`);
  triggerDownload(await res.blob(), `qr-${locationId}.${format}`);
}

export async function apiUpload<T = unknown>(path: string, formData: FormData) {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error("Unauthorized");
  }
  return res.json() as Promise<{ success: boolean; data?: T; error?: { key?: string; message: string } }>;
}

/**
 * Downloads the CSV over POST so the filters — which may include a full
 * NRIC/FIN search term — stay in the request body instead of a URL.
 */
export async function downloadCheckInsCsv(filters: unknown): Promise<void> {
  const res = await fetchAuthed("/checkins/export", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(filters),
  });
  triggerDownload(await res.blob(), "checkins.csv");
}
